/** The five most common species, each highlighted on its own small map, coloured by the metric. */
import { useMemo, useState } from 'react';
import { Modal, SimpleGrid, Stack, Text, UnstyledButton } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { viridis } from '@/theme/colors';
import { brand } from '@/theme/theme';
import { DotMap } from './DotMap';
import { useSpeciesName } from './species';
import { metricOf, metricRange, once, topSpecies } from './stats';
import type { Metric, Tree } from './types';

const MINI_MAPS = 5;

export function SpeciesMiniMaps({ trees, metric }: { trees: Tree[]; metric: Metric }) {
  const { t } = useTranslation('reports');
  const name = useSpeciesName();
  const [open, setOpen] = useState<string | null>(null);
  const species = useMemo(() => once(trees, 'top:mini', () => topSpecies(trees, MINI_MAPS, true)), [trees]);
  // One scale over all trees, so the maps compare with each other.
  const [min, max] = useMemo(() => metricRange(trees, metric), [trees, metric]);
  const colorFns = useMemo(
    () =>
      new Map(
        species.map((genus) => [
          genus,
          (x: Tree) => {
            const v = metricOf(x, metric);
            return x.genus === genus && v !== null ? viridis((v - min) / (max - min || 1)) : null;
          },
        ]),
      ),
    [species, metric, min, max],
  );

  if (!species.length) return null;
  return (
    <Stack gap="xs" className="report-minimaps">
      <Text fw={700}>{t('trees.analysis.miniMaps')}</Text>
      <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }} spacing="sm">
        {species.map((genus) => (
          <UnstyledButton key={genus} onClick={() => setOpen(genus)} aria-label={t('trees.analysis.enlarge', { species: name(genus) })}>
            <DotMap trees={trees} colorOf={colorFns.get(genus)!} width={220} height={150} radius={1.5} label={name(genus)} />
            <Text fz={12} c={brand.textMuted} ta="center">
              {name(genus)}
            </Text>
          </UnstyledButton>
        ))}
      </SimpleGrid>
      <Modal opened={open !== null} onClose={() => setOpen(null)} size="xl" title={open ? name(open) : ''}>
        {open && <DotMap trees={trees} colorOf={colorFns.get(open)!} width={900} height={600} radius={2.5} interactive label={name(open)} />}
        <Text fz={12} c={brand.textMuted} mt="xs">
          {t('trees.analysis.zoomHint')}
        </Text>
      </Modal>
    </Stack>
  );
}
